import React, { useState, useMemo, useCallback } from 'react';
import InvoiceHireForm from '../../components/Form/InvoiceHireForm';
import Button from '../../components/Form/Button';
import { PageHeader, FilterBar, ErrorState, StatsCards } from '../../components/Common';
import type { FilterConfig, StatCard } from '../../components/Common';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { InvoiceHire } from '../../types';
import { useInvoiceHires, useCreateInvoiceHire, useUpdateInvoiceHire, useDeleteInvoiceHire } from '../../hooks/useInvoiceHires';
import { useClients } from '../../hooks/useClients';
import { saveInvoiceHireItems, deleteRemovedItems, fetchInvoiceHireItems } from '../InvoiceHire/invoiceHireItemsSync';
import { bumpCodePrefixSequence } from '../../utils/codePrefixUtils';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import { useToast } from '../../contexts/ToastContext';

/** Debit and credit notes are invoice-hire records with a note type */
const isNoteType = (t?: string) => t === 'debit_note' || t === 'credit_note';

const statusBadge = (status: string) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
    status === 'paid' ? 'bg-green-100 text-green-800' :
    status === 'sent' ? 'bg-blue-100 text-blue-800' :
    status === 'overdue' ? 'bg-red-100 text-red-800' :
    status === 'cancelled' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
  }`}>{(status || 'draft').charAt(0).toUpperCase() + (status || 'draft').slice(1)}</span>
);

const DebitNotes: React.FC = () => {
  const { showToast } = useToast();
  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 1000 },
    // Populated relations let the edit form pre-fill customer/currency/FOA
    populate: '*',
  }), []);
  const { data: invoiceHires = [], isLoading, error, refetch } = useInvoiceHires(queryOptions);
  const { data: clients = [] } = useClients(queryOptions);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<InvoiceHire | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  const closeForm = () => { setShowForm(false); setEditing(undefined); };
  const createMutation = useCreateInvoiceHire(() => { showToast('Note created successfully', 'success'); refetch(); closeForm(); });
  const updateMutation = useUpdateInvoiceHire(() => { showToast('Note updated successfully', 'success'); refetch(); closeForm(); });
  const deleteMutation = useDeleteInvoiceHire(() => { showToast('Note deleted successfully', 'success'); refetch(); });

  const getClientName = useCallback((clientId: string) => {
    const client = clients.find((c: any) => c.documentId === clientId || String(c.id) === String(clientId));
    return client ? client.name : '-';
  }, [clients]);

  const notes = useMemo(() => invoiceHires.filter(h => isNoteType(h.type)), [invoiceHires]);

  const filteredNotes = useMemo(() => notes.filter(note => {
    const clientName = getClientName(note.customerId);
    const matchesSearch = !searchTerm ||
      (note.invoiceNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (note.freeTextContent || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || note.status === statusFilter;
    const matchesType = typeFilter === 'all' || note.type === typeFilter;
    const matchesClient = clientFilter === 'all' ||
      note.customerId === clientFilter ||
      String(note.customerId) === String(clientFilter);
    const d = new Date(note.invoiceDate);
    const matchesFrom = !dateFromFilter || d >= new Date(dateFromFilter);
    const matchesTo = !dateToFilter || d <= new Date(dateToFilter);
    return matchesSearch && matchesStatus && matchesType && matchesClient && matchesFrom && matchesTo;
  }), [notes, searchTerm, statusFilter, typeFilter, clientFilter, dateFromFilter, dateToFilter, getClientName]);

  const stats: StatCard[] = useMemo(() => {
    const card = (label: string, list: InvoiceHire[], color: StatCard['color']): StatCard => ({
      label, value: list.length, color,
      subtitle: list.reduce((s, n) => s + (Number(n.originalTotal ?? n.netTotal) || 0), 0).toLocaleString(),
    });
    return [
      card('Debit Notes', filteredNotes.filter(n => n.type === 'debit_note'), 'red'),
      card('Credit Notes', filteredNotes.filter(n => n.type === 'credit_note'), 'green'),
      card('Sent', filteredNotes.filter(n => n.status === 'sent'), 'blue'),
      card('Paid', filteredNotes.filter(n => n.status === 'paid'), 'green'),
      card('Total', filteredNotes, 'gray'),
    ];
  }, [filteredNotes]);

  const handleEdit = async (note: InvoiceHire) => {
    let items = note.items;
    if ((!items || items.length === 0) && note.documentId) {
      items = await fetchInvoiceHireItems(note.documentId);
    }
    setEditing({ ...note, items: items || [] });
    setShowForm(true);
  };

  const handleDelete = async (note: InvoiceHire) => {
    if (window.confirm(`Delete note ${note.invoiceNumber}?`)) {
      try { await deleteMutation.execute(note); } catch (e) { console.error(e); }
    }
  };

  const handleFormSubmit = async (data: Partial<InvoiceHire>) => {
    try {
      const fallbackDate = data.invoiceDate || new Date().toISOString().split('T')[0];
      const itemIds = await saveInvoiceHireItems(data.items || [], fallbackDate);
      if (editing) {
        await deleteRemovedItems(editing.items, itemIds);
        await updateMutation.execute({ id: editing.id, data: { ...editing, ...data, invoiceHireItems: itemIds } });
      } else {
        await createMutation.execute({ ...data, invoiceHireItems: itemIds } as any);
        await bumpCodePrefixSequence(data.invoiceNumber);
      }
    } catch (e) { console.error('Save note error:', e); }
  };

  const handleStatusUpdate = async (note: InvoiceHire, status: InvoiceHire['status']) => {
    try { await updateMutation.execute({ id: note.id, data: { ...note, status } }); } catch (e) { console.error(e); }
  };

  const buildExportData = () => filteredNotes.map(n => ({
    'Note #': n.invoiceNumber, 'Type': n.type === 'debit_note' ? 'Debit' : 'Credit',
    'Client': getClientName(n.customerId), 'Date': new Date(n.invoiceDate).toLocaleDateString(),
    'Amount': n.originalTotal ?? n.netTotal, 'Currency': resolveCurrencyCode(n.currency), 'Status': n.status,
  }));

  const filters: FilterConfig[] = [
    { key: 'type', label: 'Type', type: 'select', value: typeFilter, onChange: setTypeFilter,
      options: [{ value: 'all', label: 'All Types' }, { value: 'debit_note', label: 'Debit' }, { value: 'credit_note', label: 'Credit' }] },
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter,
      options: [{ value: 'all', label: 'All Statuses' }, { value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }, { value: 'paid', label: 'Paid' }, { value: 'overdue', label: 'Overdue' }, { value: 'cancelled', label: 'Cancelled' }] },
    { key: 'client', label: 'Client', type: 'select', value: clientFilter, onChange: setClientFilter,
      options: [{ value: 'all', label: 'All Clients' }, ...clients.map((c: any) => ({ value: c.documentId || String(c.id), label: c.name }))] },
    { key: 'dateFrom', label: 'Date From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Date To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ];
  const clearFilters = () => { setSearchTerm(''); setStatusFilter('all'); setTypeFilter('all'); setClientFilter('all'); setDateFromFilter(''); setDateToFilter(''); };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600" /></div>
  );
  if (error) return <ErrorState message="Error loading debit notes" onRetry={refetch} entityName="debit notes" />;
  if (showForm) return (
    <div className="space-y-6">
      <InvoiceHireForm invoiceHire={editing} defaultType="debit_note" onSubmit={handleFormSubmit} onCancel={closeForm} />
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Debit / Credit Notes" description="Manage debit and credit notes" />
      <StatsCards stats={stats} columns={5} />
      <FilterBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search notes..."
        filters={filters} onClearFilters={clearFilters}
        totalCount={notes.length} filteredCount={filteredNotes.length} entityName="Notes"
        onAdd={() => { setEditing(undefined); setShowForm(true); }} addLabel="Add New Note"
        onExport={() => exportToExcel(buildExportData(), `debit_notes_${new Date().toISOString().split('T')[0]}`)}
        onPrint={() => printAsExcel(buildExportData(), 'Debit / Credit Notes')}
      />
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Note #', 'Type', 'Client', 'Date', 'Amount', 'Content', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredNotes.map(note => (
                <tr key={note.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{note.invoiceNumber}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${note.type === 'debit_note' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                      {note.type === 'debit_note' ? 'Debit' : 'Credit'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getClientName(note.customerId)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(note.invoiceDate).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {(Number(note.originalTotal ?? note.netTotal) || 0).toLocaleString()} {resolveCurrencyCode(note.currency)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{note.freeTextContent || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{statusBadge(note.status)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-y-2">
                    <div className="flex space-x-2">
                      <Button onClick={() => handleEdit(note)} variant="secondary" size="sm">Edit</Button>
                      <Button onClick={() => handleDelete(note)} variant="danger" size="sm">Delete</Button>
                    </div>
                    {note.status === 'draft' && <Button onClick={() => handleStatusUpdate(note, 'sent')} variant="primary" size="sm" className="w-full text-xs">Mark Sent</Button>}
                    {note.status === 'sent' && <Button onClick={() => handleStatusUpdate(note, 'paid')} variant="primary" size="sm" className="w-full text-xs">Mark Paid</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredNotes.length === 0 && (
            <div className="text-center py-12"><p className="text-gray-500">No debit or credit notes found</p></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DebitNotes;
